import { Language } from '@yleisradio/areena-types';
import { BrazeContentCard, BrazeSdk } from 'contexts/BrazeSdkContext/types';
import getConfig from 'next/config';
import logger from './logger';
import { brazeSdkProperties } from './properties/frontend';

export async function init({
  braze,
  language,
  refreshToken,
  setContentCards,
  token,
  userId,
}: {
  braze: BrazeSdk;
  language: Language;
  refreshToken: () => void;
  setContentCards: (contentCards: BrazeContentCard[]) => void;
  token: string;
  userId: string;
}): Promise<void> {
  const { apiKey, endpoint } = brazeSdkProperties;
  const {
    publicRuntimeConfig: { versionNumber },
  } = getConfig();

  if (!apiKey) {
    throw new Error('No Braze SDK API key provided');
  }

  const success = braze.initialize(apiKey, {
    baseUrl: endpoint,
    doNotLoadFontAwesome: true,
    enableSdkAuthentication: true,
    localization: language,
    ...(versionNumber && { appVersion: versionNumber }),
  });

  if (success === false) {
    throw new Error('Failed to initialize Braze SDK');
  }

  braze.subscribeToSdkAuthenticationFailures(({ errorCode, reason }) => {
    logger.debug({ errorCode, reason }, 'Braze SDK authentication failed');
    refreshToken();
  });

  const cachedContentCards = braze.getCachedContentCards();
  setContentCards(cachedContentCards?.cards ?? []);

  braze.subscribeToContentCardsUpdates(({ cards }) => {
    setContentCards(cards);
  });

  braze.subscribeToInAppMessage((message) => {
    if (message.isControl || message instanceof braze.SlideUpMessage) {
      braze.showInAppMessage(message);
    } else {
      logger.debug(
        { inAppMessage: message },
        'In-app message type not supported'
      );
    }
  });

  braze.changeUser(userId, token);

  braze.getUser()?.setLanguage(language);

  braze.openSession();
}

export function teardown(braze: BrazeSdk): void {
  braze.wipeData();
  braze.destroy();
  window.location.reload();
}
